/**
 * PremiumPaymentGateway (src/ version)
 *
 * Redux-free version of the legacy PremiumPaymentGateway.
 * Data is sourced from React Query hooks instead of the Redux store.
 *
 * Handles the payment checkout flow as a modal.
 * Handles activation error.
 *
 * If isPremiumMidfunnelPayment is true, the payment modal will launch directly
 * (used from NextSteps upgradeToPremium flow).
 */
import { useEffect, useState } from 'react';
import { isPast } from 'date-fns';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyActivateWebApiHandler_activate,
  useRentalManagerUserPropertyDetailsWebApiHandler_details,
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../../srcLegacy/shared/constants/FreemiumConstants';
import { ACTIVATE_LISTING_MUTATION_KEY } from '../../../../srcLegacy/shared/constants/QueryConstants';

import FreemiumErrorModal from '../../modals/FreemiumErrorModal';
import PaymentModal from '../../modals/PaymentModal';
import PremiumMidfunnelSuccessModal from '../../modals/PremiumMidfunnelSuccessModal';

import { ZRM_GTM_EVENTS } from '../../../analytics/events/gtm';
import { emitCompleteUpgradeToPremiumEvent } from '../../../analytics/events/modularListing';
import { emitActivateListingEvent, emitCompletesPremiumUpsellEvent } from '../../../analytics/events/payAndPublish';
import { trackGtmEvent } from '../../../analytics/index';
import { useAuth } from '../../AuthProvider';

import type {
  FreemiumChargeResponseWebDto,
  FreemiumDetailsResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { FreemiumDatarouterError } from '../../modals/FreemiumErrorModal';

type Props = {
  listingAlias: string;
  isActive: boolean;
  freemiumData?: FreemiumDetailsResponseWebDto;
  isPremiumMidfunnelPayment?: boolean;
  displayContext?: string;
  selectedProductType?: number;
  onClose: () => void;
  isInBuildingGroup: boolean;
};

const MODAL_SWAP_DELAY = 100;

const PremiumPaymentGateway = ({
  listingAlias,
  isActive,
  freemiumData,
  isPremiumMidfunnelPayment = false,
  displayContext,
  selectedProductType = PRODUCT_TYPE_NAME_TO_ID.PREMIUM,
  onClose,
  isInBuildingGroup = false,
}: Props) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const activeProduct = freemiumData?.activeProduct;
  const activeProductType = activeProduct?.type ?? PRODUCT_TYPE_NAME_TO_ID.FREE;
  const freemiumActiveProductTimestamp = activeProduct?.expirationTimestamp ?? '';
  const products = freemiumData?.products ?? [];

  const isActiveProductExpired = freemiumActiveProductTimestamp
    ? isPast(new Date(freemiumActiveProductTimestamp))
    : true;
  const isActiveProductPremiumAndNotExpired =
    activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM && !isActiveProductExpired;

  const foundIndex = products.findIndex((product) => product.type === selectedProductType);
  const selectedProduct = foundIndex >= 0 ? products[foundIndex] : undefined;

  const { mutate: activate } = useRentalManagerUserPropertyActivateWebApiHandler_activate({
    mutationKey: ACTIVATE_LISTING_MUTATION_KEY,
  });

  const [displayPaymentModal, setDisplayPaymentModal] = useState(false);
  const [publishError, setPublishError] = useState<{
    display: boolean;
    error?: FreemiumDatarouterError;
    onClose?: () => void;
  }>({ display: false });
  const [displayMidfunnelSuccessModal, setDisplayMidfunnelSuccessModal] = useState(false);

  // Run only on mount: open the payment modal unless the active product is already premium.
  // We intentionally omit isActiveProductPremiumAndNotExpired from deps to avoid re-triggering.
  useEffect(() => {
    if (isActiveProductPremiumAndNotExpired) {
      return;
    }
    setDisplayPaymentModal(true);
  }, []);

  const invalidatePropertyCache = () => {
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_overview.getKey({ listingAlias }),
    });
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_details.getKey({ listingAlias }),
    });
    queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' as const });
  };

  const handleActivationError = (error: FreemiumDatarouterError) => {
    setPublishError({
      display: true,
      error,
      onClose: () => {
        onClose();
        setPublishError({ display: false });
      },
    });
  };

  const activateListing = (freemiumChargeResponse?: FreemiumChargeResponseWebDto) => {
    return activate(
      { getParams: { listingAlias } },
      {
        onSuccess: (activateResponse) => {
          const csEventParams = {
            currentProperty: { listingAlias },
            user: user ?? {},
          };

          emitActivateListingEvent(csEventParams);

          if (freemiumChargeResponse) {
            trackGtmEvent({ event: ZRM_GTM_EVENTS.RENTAL_MANAGER_PAY_PUBLISH });
            emitCompletesPremiumUpsellEvent(csEventParams);
          } else {
            trackGtmEvent({ event: ZRM_GTM_EVENTS.RENTAL_MANAGER_PUBLISH });
            onClose();
          }

          void activateResponse;
          invalidatePropertyCache();
        },
        onError: (error) => {
          handleActivationError(error as FreemiumDatarouterError);
        },
      },
    );
  };

  const handleFreemiumChargeSuccess = (response: FreemiumChargeResponseWebDto) => {
    if (response.isPaymentValid) {
      if (!isPremiumMidfunnelPayment && !isActive) {
        activateListing(response);

        emitCompleteUpgradeToPremiumEvent({
          currentProperty: { listingAlias },
          user: user ?? {},
          triggerSource: 'zrm_premium_confirm_upgrade_at_activation',
        });
      } else {
        setDisplayMidfunnelSuccessModal(true);
        setDisplayPaymentModal(false);

        emitCompletesPremiumUpsellEvent({
          currentProperty: { listingAlias },
          user: user ?? {},
        });

        if (!isActive) {
          emitCompleteUpgradeToPremiumEvent({
            currentProperty: { listingAlias },
            user: user ?? {},
            triggerSource: 'zrm_premium_confirm_upgrade_preactivation',
          });
        }

        invalidatePropertyCache();
      }
    }
  };

  const handleFreemiumErrorPublishForFreeClick = () => {
    setPublishError({ display: false });
    activateListing();
  };

  const handleFreemiumErrorTryAgainClick = () => {
    setPublishError({ display: false });

    setTimeout(() => {
      setDisplayPaymentModal(true);
    }, MODAL_SWAP_DELAY);
  };

  const handleFreemiumChargeError = (err: FreemiumDatarouterError) => {
    setDisplayPaymentModal(false);

    setTimeout(() => {
      setPublishError({
        display: true,
        error: err,
        onClose: () => {
          onClose();
          setPublishError({ display: false });
        },
      });
    }, MODAL_SWAP_DELAY);
  };

  void displayContext;
  void selectedProduct;

  return (
    <div data-testid="PremiumPaymentGateway">
      {publishError.display ? (
        <FreemiumErrorModal
          error={publishError?.error}
          onClose={publishError?.onClose ?? onClose}
          onTryAgainClick={handleFreemiumErrorTryAgainClick}
          onPublishForFreeClick={handleFreemiumErrorPublishForFreeClick}
          isPremiumMidfunnelPayment={isPremiumMidfunnelPayment}
          selectedProductType={selectedProductType}
        />
      ) : null}
      {displayPaymentModal && (
        <PaymentModal
          handleFreemiumChargeSuccess={handleFreemiumChargeSuccess}
          onFreemiumChargeError={handleFreemiumChargeError}
          listingAlias={listingAlias}
          isRenderedInPageView={false}
          onClose={() => {
            setDisplayPaymentModal(false);
            onClose();
          }}
          isInBuildingGroup={isInBuildingGroup}
        />
      )}
      {displayMidfunnelSuccessModal && (
        <PremiumMidfunnelSuccessModal
          onClose={() => {
            setDisplayMidfunnelSuccessModal(false);
            onClose();
          }}
          isActive={isActive}
        />
      )}
    </div>
  );
};

export default PremiumPaymentGateway;
